import React from 'react';
import { message, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import moment from 'moment';
import type { Dayjs } from 'dayjs';

import { EPCheckbox, EPRangePicker } from '@/comp';
import { useStores } from '@/hooks/use-stores';
import { useInvestigationDateUrlUpdate } from '@/hooks/useInvestigationDateUrlUpdate';
import IconExport from '@/assets/icon-export.svg?react';

import DataExportModal from '../DataExportModal/DataExportModal';
import DropdownAlarm from '../DropdownAlarm/DropdownAlarm';
import ButtonStacked from '../ButtonStacked';
import { getDisabledDatesForSecondPickerFunc } from './ActionBar.utils';
import { CheckboxRow } from './ActionBar.styles';

const ActionBar = observer(({ isStackMode, setIsStackMode, setSelectedAlarm }) => {
  const { investigationStore } = useStores();
  const { t } = useTranslation();
  const { updateDatesInUrl } = useInvestigationDateUrlUpdate();

  const onChangeDate1 = (dates: [Dayjs, Dayjs] | null) => {
    if (dates && dates[0] && dates[1]) {
      const momentDates: [moment.Moment, moment.Moment] = [moment(dates[0].toDate()), moment(dates[1].toDate())];
      investigationStore.setDates1(momentDates);
      updateDatesInUrl(momentDates, investigationStore.dates2);
    } else {
      investigationStore.setDates1(null);
      updateDatesInUrl(null, investigationStore.dates2);
    }
  };

  const onClickStackModeIcon = () => {
    if (!investigationStore.allowStacking) return;

    setIsStackMode(value => !value);
  };

  const onChangeDropdownAlarm = key => {
    setSelectedAlarm(key);
  };

  const onChangeDate2 = (dates?: [Dayjs, Dayjs] | null) => {
    if (dates && dates[0]) {
      const startMoment = moment(dates[0].toDate());
      const endMoment = dates[1] ? moment(dates[1].toDate()) : null;
      const momentDates = endMoment ? [startMoment, endMoment] : [startMoment, null];

      investigationStore.setDates2(momentDates);
      updateDatesInUrl(investigationStore.dates1, investigationStore.dates2);
    } else {
      investigationStore.setDates2(null);
      updateDatesInUrl(investigationStore.dates1, null);
    }
  };

  const showEngineStatusUnavailableMessage = () => {
    message.info(t('engineStatusNotAvailable'));
  };

  const onChangeCompareMode = (e: React.ChangeEvent<HTMLInputElement>) => {
    investigationStore.setIsCompareMode(e.target.checked);
    if (e.target.checked && investigationStore.showEngineStatus) {
      investigationStore.setShowEngineStatus(false);
      showEngineStatusUnavailableMessage();
    }
    if (!e.target.checked) {
      investigationStore.setDates2(null);
    }
    updateDatesInUrl(investigationStore.dates1, investigationStore.dates2);
  };

  const onChangeShowEngineStatus = e => {
    if (e.target.checked && investigationStore.isCompareMode) {
      showEngineStatusUnavailableMessage();
      return;
    }
    investigationStore.setShowEngineStatus(e.target.checked);
  };

  return (
    <div className="action-bar">
      <div className="flex align-end justify-between">
        <div className="flex align-end">
          <div className="action-bar-item">
            <div>
              <div className="label-range">{t('Show me (from-to)')}</div>
              <EPRangePicker
                allowClear={true}
                value={investigationStore.dates1}
                onChange={onChangeDate1}
                useRangeLimiters={true}
                disableFutureDates={true}
              />
            </div>
          </div>
          {investigationStore.isCompareMode && (
            <>
              <div className="action-bar-item vs">{t('VS')}</div>
              <div className="action-bar-item">
                <Popover
                  placement={'bottomLeft'}
                  content={t('When comparing 2 time periods, you can choose up to 5 parameters.')}
                  {...(investigationStore.totalMetricsSelected > 5 ? {} : { open: false })}
                >
                  <div>
                    <div className="label-range">{t('Compare with (Show me from-to)')}</div>

                    <EPRangePicker
                      useRangeLimiters={false}
                      startDateDisabledDate={getDisabledDatesForSecondPickerFunc(investigationStore.dates1)}
                      onOpenHideCurrentValue={true}
                      allowClear={true}
                      allowEmpty={[false, true]}
                      disabled={[
                        !investigationStore.dates1 ||
                          !investigationStore.dates1[0] ||
                          !investigationStore.dates1[1] ||
                          investigationStore.totalMetricsSelected > 5,
                        true,
                      ]}
                      value={investigationStore.dates2}
                      className={'range-picker2'}
                      onChange={onChangeDate2}
                      disableFutureDates={true}
                    />
                  </div>
                </Popover>
              </div>
            </>
          )}
        </div>

        <div className="flex">
          <DataExportModal>
            <div className="action-bar-item export-btn">
              <IconExport />
            </div>
          </DataExportModal>

          <div className="action-bar-item">
            <DropdownAlarm onChange={onChangeDropdownAlarm} />
          </div>
          <div className="action-bar-item">
            <ButtonStacked
              active={isStackMode}
              disabled={!investigationStore.allowStacking}
              onClick={onClickStackModeIcon}
            />
          </div>
        </div>
      </div>
      <CheckboxRow>
        <EPCheckbox checked={investigationStore.isCompareMode} onChange={onChangeCompareMode}>
          {t('compareTwoTimePeriods')}
        </EPCheckbox>
        {investigationStore.hasEngine && (
          <EPCheckbox checked={investigationStore.showEngineStatus} onChange={onChangeShowEngineStatus}>
            {t('addEngineStatusOverlay')}
          </EPCheckbox>
        )}
      </CheckboxRow>
    </div>
  );
});

export default ActionBar;
